import React from "react";
import { Tooltip } from "@mui/material";
import WarningIcon from "@mui/icons-material/Warning";
import { checkedCircleIconSmall } from "../../../common/checkedCircleIcon";
import {
  KeyframeIntervalFields,
  keyframeMeasuredText,
  summarizeKeyframeInterval,
} from "./SiteAboutPageUtils/keyframeInterval";

/**
 * "Keyframe interval" line of the Camera Settings block (ENG-2222), shared by
 * the site camera list and Edit Camera. Green at or under 4 s, orange above,
 * and a bare "—" (no icon) until the connector has measured it. A lower
 * bound ("> 12.0 s") warns the same way above 4 s; at or under 4 s it shows
 * the value with no icon, since the real interval is not known yet.
 */
export const KeyframeIntervalRow = ({
  status,
  nowMs,
}: {
  status: KeyframeIntervalFields | null | undefined;
  nowMs?: number;
}) => {
  const summary = summarizeKeyframeInterval(status);
  const measured = keyframeMeasuredText(summary.measuredAt, nowMs);
  const tooltip =
    summary.level === "warning"
      ? [summary.hint, measured ? `(${measured})` : null]
          .filter(Boolean)
          .join(" ")
      : summary.level === "ok"
      ? [
          "Live view starts within a few seconds on this camera.",
          measured ? `(${measured})` : null,
        ]
          .filter(Boolean)
          .join(" ")
      : "";

  const icon =
    summary.level === "warning" ? (
      <WarningIcon
        sx={{ color: "#FF8400" }}
        fontSize="small"
        data-testid="keyframe-interval-warning"
      />
    ) : summary.level === "ok" ? (
      <span data-testid="keyframe-interval-ok">{checkedCircleIconSmall}</span>
    ) : (
      // Keeps the label aligned with its siblings without implying a verdict.
      <span className="inline-block w-[20px]" aria-hidden />
    );

  return (
    <div
      className="font-mulish text-[12px] text-black pt-[10px] flex"
      data-testid="keyframe-interval-row"
    >
      <div>
        {tooltip ? (
          <Tooltip
            title={tooltip}
            placement="top"
            sx={{ fontFamily: "mulish" }}
          >
            {icon}
          </Tooltip>
        ) : (
          icon
        )}
        &nbsp;&nbsp;Keyframe interval
      </div>
      <div
        className="font-mulish font-bold text-[12px] text-black pl-[12px]"
        data-testid="keyframe-interval-value"
      >
        {summary.display}
      </div>
    </div>
  );
};
